import type { Projection, ScreenPos, WorldPos } from "./Projection";

const TILE_WIDTH = 64;
const TILE_HEIGHT = 32;
const HALF_W = TILE_WIDTH / 2;
const HALF_H = TILE_HEIGHT / 2;

export class IsoProjection implements Projection {
  readonly kind = "iso" as const;
  readonly tileWidth = TILE_WIDTH;
  readonly tileHeight = TILE_HEIGHT;

  worldToScreen(x: number, y: number): ScreenPos {
    return { sx: (x - y) * HALF_W, sy: (x + y) * HALF_H };
  }

  tileFoot(x: number, y: number): ScreenPos {
    return this.worldToScreen(x, y);
  }

  screenToWorld(sx: number, sy: number): WorldPos {
    return {
      x: sx / TILE_WIDTH + sy / TILE_HEIGHT,
      y: sy / TILE_HEIGHT - sx / TILE_WIDTH,
    };
  }

  defaultOrigin(viewportW: number, viewportH: number, gridSize: number): ScreenPos {
    const middle = this.worldToScreen(gridSize / 2, gridSize / 2);
    return {
      sx: viewportW / 2 - middle.sx,
      sy: viewportH / 2 - middle.sy,
    };
  }

  depthKey(x: number, y: number): number {
    return (x + y) * 1000;
  }

  rotateInputToWorld(rx: number, ry: number): { dx: number; dy: number } {
    const inv = 1 / Math.SQRT2;
    return {
      dx: (rx + ry) * inv,
      dy: (-rx + ry) * inv,
    };
  }

  /**
   * Atlas drawn as if viewer looks at iso scene → rotate world dir 45° clockwise
   * to get on-screen facing.
   */
  worldDirToFrame(dir: number): number {
    return (((dir + 1) % 8) + 8) % 8;
  }
}
